import { DashboardIconNew } from '../svgs/DashboardIconNew';
import { ExploreIcon } from '../svgs/ExploreIcon';
import { FeedbackIcon } from '../svgs/FeedbackIcon';
import { HomeIcon } from '../svgs/HomeIcon';
import { LogoutIcon } from '../svgs/LogoutIcon';
import { MarketScanIcon } from '../svgs/MarketScanIcon';
import { SettingsIcon } from '../svgs/SettingsIcon';
import { WorkplaceIcon } from '../svgs/WorkplaceIcon';
import { AdviserIcon } from '../svgs/AdviserIcon';
import Logo from 'components/logo';
import {
  ADMIN_ROUTE,
  ADVICE_ROUTE,
  DASHBOARD_ROUTE,
  EXPLORE_ROUTE,
  HOME_ROUTE,
  MARKET_SCAN_ROUTE,
  NOTIFICATIONS_ROUTE,
  OPERATIONS_ROUTE,
  PROFILE_ROUTE,
  WORKPLACE_ROUTE,
} from 'constants/routes';
import { UserRoles } from 'constants/user';
import { useClickOutside } from 'hooks/useClickOutside';
import React from 'react';
import { Bell, ChevronLeft, X, Star } from 'react-feather';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Popup } from 'semantic-ui-react';
import { IUser } from 'types';
import { useAuth0 } from '../../react-auth0-spa';
import styles from './sidebar.scss';
import { isBreakpoint, isSafari } from 'helpers';

// Wrapper to match Bell icon size with other sidebar icons
const NotificationIcon = ({ className }: { className?: string }) => (
  <Bell className={className} size={20} />
);

interface ISideBarProps {
  className?: string;
  showSetup?: boolean;
  mobile: boolean;
  small: boolean;
  user: IUser;
  openSidebar(): void;
  closeSidebar(): void;
  closeMobileSidebar(): void;
}

const menuItems = [
  // User items
  {
    route: HOME_ROUTE,
    icon: HomeIcon,
    exact: true,
    label: 'Home',
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
  {
    route: DASHBOARD_ROUTE,
    icon: DashboardIconNew,
    label: 'My Dashboard',
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
  {
    route: MARKET_SCAN_ROUTE,
    icon: MarketScanIcon,
    label: 'Market Scan',
    className: styles.stroke,
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
  // {
  //   route: ADVICE_ROUTE,
  //   icon: AdviserIcon,
  //   label: 'Get advice',
  //   roles: [UserRoles.ADMIN, UserRoles.USER],
  // },
  {
    route: EXPLORE_ROUTE,
    icon: ExploreIcon,
    label: 'Explore Insurance',
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
  {
    route: NOTIFICATIONS_ROUTE,
    icon: NotificationIcon,
    label: 'Notifications',
    className: styles.noFill,
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },

  // Admin items
  {
    route: ADMIN_ROUTE,
    icon: SettingsIcon,
    label: 'Admin',
    className: styles.noFill,
    roles: [UserRoles.ADMIN],
  },
  {
    route: OPERATIONS_ROUTE,
    icon: SettingsIcon,
    label: 'Operations',
    className: styles.noFill,
    roles: [UserRoles.ADMIN, UserRoles.SUPPORT],
  },
];

const bottomMenuItems = [
  {
    route: PROFILE_ROUTE,
    getIcon: ({ user }) => <img className={styles.avatar} src={user.picture} />,
    getLabel: ({ user }) => `${user.firstName} ${user.lastName}`,
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
    excludes: [WORKPLACE_ROUTE],
  },
  {
    route: 'mailto:team@quashed.co.nz',
    icon: FeedbackIcon,
    label: 'Submit feedback',
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
  {
    className: styles.noFill,
    icon: LogoutIcon,
    label: 'Log out',
    onClick: (_, { logout }) => logout(),
    roles: [UserRoles.ADMIN, UserRoles.USER, UserRoles.SUPPORT],
  },
];

const isActiveRoute = (pathname: string, { exact, excludes = [], route }) => {
  if (exact) {
    return pathname === route;
  }

  // Only return false when the current pathname includes an excluded route
  if (excludes.length) {
    const hasExcluded = excludes.some((exclude) => pathname.includes(exclude));
    if (hasExcluded) {
      return false;
    }
  }

  return pathname.includes(route);
};

const MenuItem = (props) => {
  const {
    className,
    label,
    icon: MenuIcon,
    getIcon,
    roles,
    route,
    getLabel,
    skip = R.F,
    onClick,
  } = props.item;

  const isRoute = isActiveRoute(props.pathname, props.item);

  const iconClassName = classnames(
    styles.icon,
    className,
    isRoute && styles.activeIcon,
  );

  if ((roles && !R.includes(props.role, roles)) || skip(props.user)) {
    return null;
  }

  const l = getLabel ? getLabel(props) : label;
  const icon = getIcon ? (
    getIcon(props)
  ) : (
    <MenuIcon className={iconClassName} />
  );

  const handleClick = (e) => {
    if (onClick) {
      onClick(e, props);
    } else {
      props.closeMobileSidebar();
    }
  };

  const trigger = route?.startsWith('mailto:') ? (
    <a
      className={classnames(styles.menuItem, isRoute && styles.active)}
      href={route}
      target="_blank"
    >
      <div className={styles.iconContainer}>{icon}</div>
      <span>{l}</span>
    </a>
  ) : (
    <Link
      className={classnames(styles.menuItem, isRoute && styles.active)}
      to={route || '#'}
      onClick={handleClick}
    >
      <div className={styles.iconContainer}>{icon}</div>
      <span>{l}</span>
    </Link>
  );

  if (!props.small || props.mobile) {
    return trigger;
  }

  return (
    <Popup
      key={l}
      position="right center"
      trigger={trigger}
      content={l}
      hideOnScroll={true}
    />
  );
};

const Menu = React.memo((props) => {
  const { pathname } = useLocation();

  const isMobile = isBreakpoint('xs') || isBreakpoint('sm');

  return (
    <div className={styles.menu}>
      <X
        className={styles.close}
        size={26}
        onClick={props.closeMobileSidebar}
      />
      <div className={styles.brand}>
        <Link to={HOME_ROUTE} onClick={props.closeMobileSidebar}>
          <Logo
            sub={!isMobile}
            small={!props.mobile && !isMobile && props.small}
          />
        </Link>
      </div>
      <div className={styles.menuItems}>
        <div className={styles.top}>
          {menuItems.map((item, idx) => {
            return (
              <MenuItem
                key={`menuItem_${idx}`}
                {...props}
                item={item}
                pathname={pathname}
              />
            );
          })}
        </div>
        <div className={styles.bottom}>
          {bottomMenuItems.map((item, idx) => {
            return (
              <MenuItem
                key={`bottomMenu_${idx}`}
                {...props}
                item={item}
                pathname={pathname}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});

const SideBar = ({
  className,
  mobile,
  small,
  user,
  openSidebar,
  closeSidebar,
  closeMobileSidebar,
}: ISideBarProps) => {
  const ref = useClickOutside(closeMobileSidebar);
  const { getRole, logout } = useAuth0();
  const role = getRole();

  const containerClassName = classnames(
    styles.container,
    small && styles.small,
    mobile && styles.mobile,
    isSafari() && styles.noTransition,
    className,
  );

  return (
    <div className={containerClassName} ref={mobile ? ref : null}>
      <div className={styles.tab} onClick={small ? openSidebar : closeSidebar}>
        <ChevronLeft size={20} />
      </div>
      <Menu
        role={role}
        user={user}
        small={small}
        mobile={mobile}
        closeMobileSidebar={closeMobileSidebar}
        logout={logout}
      />
    </div>
  );
};

export default React.memo(SideBar);
